import { Chatbox } from '@talkjs/react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { QueryKey } from '@tanstack/react-query';
import { Dispatch, SetStateAction } from 'react';
import ChatBoxHeader from './ChatBoxHeader';
import { UserConversation } from '../../types/chats';
import useFeatureGuard from '../../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../../types/subscriptions';
import useSaveChatPhoto from '../../hooks/chat/useSaveChatPhoto';

const IMAGE =
  'https://firebasestorage.googleapis.com/v0/b/bamboo-artwork-372213.appspot.com/o/public%2Fassets%2Fempty-chatbox.png?alt=media';

interface Props {
  selectedConversation: UserConversation | null;
  onSelectConversation: (conversation: UserConversation | null) => void;
  queryKey: QueryKey;
  updateSelectedConversation: Dispatch<SetStateAction<UserConversation | null>>;
}

function ChatBox({
  selectedConversation,
  onSelectConversation,
  queryKey,
  updateSelectedConversation,
}: Props) {
  const canChat = useFeatureGuard(Feature.CHAT);
  const { onCustomMessageAction } = useSaveChatPhoto(selectedConversation);

  return (
    <Box width="100%" height="100%" id="porter-chatbox">
      {selectedConversation && (
        <Stack justifyContent="stretch" height="100%" width="100%">
          <ChatBoxHeader
            conversation={selectedConversation}
            onBack={() => onSelectConversation(null)}
            updateConversation={updateSelectedConversation}
            queryKey={queryKey}
          />
          <Chatbox
            conversationId={selectedConversation.id}
            style={{
              flex: 1,
            }}
            theme="business_web"
            messageField={{ autofocus: false, visible: canChat }}
            loadingComponent={
              <Box
                width="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                mt={2}>
                <CircularProgress color="primary" size={20} />
              </Box>
            }
            onCustomMessageAction={onCustomMessageAction}
          />
        </Stack>
      )}
      {!selectedConversation && (
        <Box
          boxSizing="border-box"
          p={2}
          width="100%"
          height="100%"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={1}>
          <img
            src={IMAGE}
            alt="Empty chatbox"
            style={{
              width: '100%',
              maxWidth: '200px',
              height: 'auto',
            }}
          />
          <Typography
            textAlign="center"
            mt={2}
            variant="h1"
            component="p"
            color="text.primary">
            Pick up where you left off
          </Typography>
          <Typography textAlign="center" variant="body1" color="text.primary">
            Select a conversion and chat away.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default ChatBox;
